import { AccountInfo, PublicClientApplication, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { Authenticator, LoginResult, User, UserNameType } from './authenticator';

export interface EntraConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes: string[] | string;
}

let globalEntraConfig: EntraConfig | undefined;

export function configureEntra(config: EntraConfig) {
  // Ensure default scopes if not provided
  globalEntraConfig = {
    ...config,
    scopes: normalizeScopes(config.scopes)
  };
}

export class EntraAuthenticator extends Authenticator {
  private readonly pca: PublicClientApplication;
  private readonly scopes: string[];
  private readonly initialized: Promise<void>;

  constructor(config?: EntraConfig) {
    super();
    const effectiveConfig: EntraConfig | undefined = config ?? globalEntraConfig;
    if (!effectiveConfig) {
      throw new Error('Entra configuration missing');
    }
    this.pca = new PublicClientApplication({
      auth: {
        clientId: effectiveConfig.clientId,
        authority: `https://login.microsoftonline.com/${effectiveConfig.tenantId}`,
        redirectUri: effectiveConfig.redirectUri,
        postLogoutRedirectUri: effectiveConfig.logoutRedirectUri ?? effectiveConfig.redirectUri,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'localStorage'
      },
      system: {
        loggerOptions: {
          loggerCallback: () => {
            // Silent - no logging
          },
          piiLoggingEnabled: false
        }
      }
    });
    this.scopes = ensureAuthScopes(normalizeScopes(effectiveConfig.scopes));

    // Initialize MSAL and handle any pending redirect responses
    this.initialized = this.pca
      .initialize()
      .then(async () => {
        await this.processRedirectWithRetry();
      })
      .catch(() => undefined);
  }

  private async ensureInitialized(): Promise<void> {
    await this.initialized;
  }

  private async processRedirectWithRetry(): Promise<void> {
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const result = await this.pca.handleRedirectPromise();
      if (result?.account) {
        this.pca.setActiveAccount(result.account);

        return;
      }

      const accounts = this.pca.getAllAccounts();
      if (accounts.length > 0) {
        this.pca.setActiveAccount(accounts[0]);

        return;
      }

      const hasCode = window.location.hash.includes('code=');

      if (!hasCode) {
        return;
      }

      await new Promise((r) => setTimeout(r, attempt * 200));
    }
  }

  private getActiveAccount(): AccountInfo | null {
    const accounts = this.pca.getAllAccounts();

    return accounts.length > 0 ? accounts[0] : null;
  }

  private async acquireToken(): Promise<string | null> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    const silentRequest: SilentRequest = {
      account,
      scopes: this.scopes
    };

    try {
      const result = await this.pca.acquireTokenSilent(silentRequest);

      return result.accessToken;
    } catch (e) {
      return null;
    }
  }

  async getUser(): Promise<User> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    let accessToken = await this.acquireToken();

    if (!accessToken) {
      // Not authenticated or interaction required; provide empty token
      accessToken = '';
    }

    // Derive email/username from ID token claims if present
    const email = (account?.idTokenClaims as { preferred_username: string })?.preferred_username || (account?.idTokenClaims as { upn: string })?.upn || '';

    const user: User = {
      email,
      authorizationHeader: accessToken ? `Bearer ${accessToken}` : ''
    };

    return user;
  }

  async getAuthenticationHeader(): Promise<string> {
    const user = await this.getUser();
    return user.authorizationHeader;
  }

  async isAuthenticated(): Promise<boolean> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    const isAuth = !!account;

    return isAuth;
  }

  async login(_: string, __: string): Promise<LoginResult> {
    await this.ensureInitialized();
    const request: RedirectRequest = {
      scopes: this.scopes
    };
    await this.pca.loginRedirect(request);
    // Redirect will occur; returning a non-logged-in state here is fine
    return { isLoggedIn: false, totpSetupDetails: { authenticatorCode: '', qrCodeUrl: '' } };
  }

  userNameType(): UserNameType {
    return UserNameType.EMAIL;
  }

  requiresCredentials(): boolean {
    return false;
  }

  enableForgotPassword(): boolean {
    return false;
  }

  async forgotPassword(_username: string): Promise<void> {
    return Promise.reject(new Error('Not implemented'));
  }

  async forgotPasswordConfirm(_username: string, _password: string, _code: string): Promise<void> {
    return Promise.reject(new Error('Not implemented'));
  }

  async logout(): Promise<void> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    await this.pca.logoutRedirect({ account: account ?? undefined });
  }

  getPasswordPolicy(): undefined {
    return undefined;
  }
}

function normalizeScopes(input?: string[] | string): string[] {
  if (!input) {
    return ['User.Read'];
  }
  if (Array.isArray(input)) {
    const arr = input.map((s) => s.trim()).filter(Boolean);
    return arr.length > 0 ? arr : ['User.Read'];
  }
  const parts = input
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : ['User.Read'];
}

function ensureAuthScopes(scopes: string[]): string[] {
  const set = new Set(scopes);
  // Ensure OIDC scopes so MSAL can establish account/session
  set.add('openid');
  set.add('profile');
  set.add('email');
  return Array.from(set);
}
